import { http } from "../../../shared/api/http"

export type DateRangeFilters = {
  from_date?: string
  to_date?: string
}

/* ===================== OVERVIEW ===================== */

export type AdminOverview = {
  users: { total: number; new_last_30_days: number; mentors: number; active_last_7_days: number }
  courses: { total: number; total_views: number; total_purchases: number; avg_conversion_rate: number }
  vacancies: { total: number; total_views: number; total_applications: number; avg_application_rate: number }
  china_enterprises: { total: number; total_views: number }
  china_markets: { total: number; total_views: number }
}

export const getAdminOverviewApi = () =>
  http.get<AdminOverview>("/api/admin/analytics/overview")

/* ===================== USERS ===================== */

export type AdminUsersAnalytics = {
  total: number
  new_by_day: { date: string; count: number }[]
  by_role?: { user?: number; mentor?: number; admin?: number }
  by_city: { city: string; count: number }[]
  active_last_7_days: number
}

export const getAdminUsersAnalyticsApi = (filters: DateRangeFilters = {}) =>
  http.get<AdminUsersAnalytics>("/api/admin/analytics/users", { params: filters })

/* ===================== COURSES ===================== */

export type AdminCourseListItem = {
  course_id: string
  title: string
  author_id: string
  author_name: string
  views: number
  unique_visitors: number
  purchases: number
  conversion_rate: number
  avg_rating: number
  reviews_count: number
  completion_rate: number
  revenue: number
  is_active: boolean
}

export type AdminCoursesListResponse = {
  total: number
  items: AdminCourseListItem[]
}

export type AdminCoursesFilters = DateRangeFilters & {
  sort_by?: "views" | "purchases" | "conversion_rate" | "rating"
  limit?: number
  offset?: number
}

export const getAdminCoursesApi = (filters: AdminCoursesFilters = {}) =>
  http.get<AdminCoursesListResponse>("/api/admin/analytics/courses", { params: filters })

export type AdminCourseDetail = {
  course_id: string
  title: string
  views: number
  unique_visitors: number
  purchases: number
  avg_rating: number
  reviews_count: number
  completion_rate: number
  revenue: number
  views_by_day: { date: string; count: number }[]
  purchases_by_day: { date: string; count: number }[]
}

export const getAdminCourseDetailApi = (courseId: string) =>
  http.get<AdminCourseDetail>(`/api/admin/analytics/courses/${courseId}`)

export type CourseDropoutLesson = {
  lesson_id: string
  title: string
  avg_dropout_percent: number
  completion_rate: number
  total_viewers: number
}

export type CourseDropoutModule = {
  module_id: string
  title: string
  lessons: CourseDropoutLesson[]
}

export type CourseDropout = {
  course_id: string
  modules: CourseDropoutModule[]
}

export const getAdminCourseDropoutApi = (courseId: string) =>
  http.get<CourseDropout>(`/api/admin/analytics/courses/${courseId}/dropout`)

/* ===================== MENTORS ===================== */

export type AdminMentorItem = {
  mentor_id: string
  full_name: string
  avatar_url: string | null
  courses_count: number
  total_views: number
  total_purchases: number
  avg_conversion_rate: number
  avg_rating: number
  profile_views: number
  total_revenue: number
}

export type AdminMentorsResponse = {
  items: AdminMentorItem[]
}

export const getAdminMentorsApi = () =>
  http.get<AdminMentorsResponse>("/api/admin/analytics/mentors")

export type AdminMentorCourseRow = {
  course_id: string
  title: string
  views: number
  purchases: number
  revenue: number
  avg_rating: number
  reviews_count: number
  completion_rate: number
}

export type AdminMentorDetail = {
  mentor_id: string
  full_name: string
  avatar_url: string | null
  profile_views: number
  courses_count: number
  courses: AdminMentorCourseRow[]
  views_by_day: { date: string; count: number }[]
  total_views: number
  total_purchases: number
  total_revenue: number
  avg_rating: number
  avg_conversion_rate: number
}

export const getAdminMentorDetailApi = (mentorId: string) =>
  http.get<AdminMentorDetail>(`/api/admin/analytics/mentors/${mentorId}`)

/* ===================== VACANCIES ===================== */

export type AdminVacancyItem = {
  id: string
  title: string
  creator_id: string
  views: number
  applications: number
  application_rate: number
  is_active: boolean
  created_at: string
}

export type AdminVacanciesResponse = {
  total: number
  items: AdminVacancyItem[]
}

export const getAdminVacanciesAnalyticsApi = () =>
  http.get<AdminVacanciesResponse>("/api/admin/analytics/vacancies")

export type AdminVacancyDetail = {
  vacancy_id: string
  title: string
  creator_id: string
  views: number
  applications: number
  application_rate: number
  applications_by_day: { date: string; count: number }[]
}

export const getAdminVacancyDetailApi = (vacancyId: string) =>
  http.get<AdminVacancyDetail>(`/api/admin/analytics/vacancies/${vacancyId}`)

/* ===================== CHINA ENTERPRISES ===================== */

export type AdminChinaEnterpriseItem = {
  enterprise_id: string
  name: string
  views: number
  favorites_count: number
  reviews_count: number
  avg_rating: number
  moderation_status: string
}

export type AdminChinaResponse = {
  total: number
  items: AdminChinaEnterpriseItem[]
}

export const getAdminChinaAnalyticsApi = () =>
  http.get<AdminChinaResponse>("/api/admin/analytics/china")

/** Narrow analytics-only view of the enterprise detail endpoint (same endpoint as the edit form). */
export type AdminChinaEnterpriseDetail = {
  id: string
  name: string
  views: number
  views_by_day: { date: string; count: number }[]
  favorites_count: number
  average_rating: number
  reviews_count: number
}

export const getAdminChinaEnterpriseDetailApi = (enterpriseId: string) =>
  http.get<AdminChinaEnterpriseDetail>(`/api/admin/china/enterprises/${enterpriseId}`)

/* ===================== CHINA MARKETS ===================== */

export type AdminChinaMarketItem = {
  market_id: string
  name: string
  views: number
  favorites_count: number
  reviews_count: number
  avg_rating: number
  moderation_status: string
}

export type AdminChinaMarketsResponse = {
  total: number
  items: AdminChinaMarketItem[]
}

export type AdminChinaMarketsFilters = {
  limit?: number
  offset?: number
}

export const getAdminChinaMarketsAnalyticsApi = (filters: AdminChinaMarketsFilters = {}) =>
  http.get<AdminChinaMarketsResponse>("/api/admin/analytics/china/markets", { params: filters })
